import React, { useEffect, useRef, useState } from 'react';
import { defineMessages, FormattedMessage, injectIntl, RawIntlProvider } from 'react-intl';
import { Popover, Overlay } from 'react-bootstrap';
import classnames from 'classnames';


import './CraneResult.css';
import CheckCircle from 'components/CheckCircle';
import moment from 'moment';
import MomentLocaleUtils, {
	formatDate,
	parseDate,
} from 'shared/utils/MomentLocaleUtils';
import styled from 'styled-components';
import { isValid, parse } from 'date-fns';
import { usePopper } from 'react-popper';
import { enUS } from 'date-fns/locale';
import InputCalendar, { isDisabledDate } from 'components/InputCalendar';


const CraneResult = (props) => {
	moment.locale(props.userLocale);
	const inspectionPassRef = useRef();
	const inspectionDateRef = useRef();
	const attestRef = useRef();
	const signatureOverlayRef = useRef();
	const signatureRef = useRef();

	const handlePassCheckCircleClick = (name, checked) => {
		if (!checked)
			props.setFormData('hasInspectionPassed', true);
		else
			props.setFormData('hasInspectionPassed', null);
	}

	const handleFailCheckCircleClick = (name, checked) => {
		if (!checked) {
			props.setFormData('hasInspectionPassed', false);
			props.setFormData('inspectorAttested', false);

			if (props.onInspectionFailClick)
				props.onInspectionFailClick();
		}
		else
			props.setFormData('hasInspectionPassed', null);
	}

	const onClickAttest = () => {
		props.setFormData('inspectorAttested',
			!props.formData.get('inspectorAttested'));
	}

	const handleChange = (event) => {
		event.preventDefault();
		props.setFormData(event.currentTarget.id, event.currentTarget.value);
	}

	const onDateChangeHandler = (selectedDay, isDisabledFromCalendar = false) => {
		const date = selectedDay !== undefined ? new Date(moment(selectedDay)) : undefined;
        const isDisabled = isDisabledFromCalendar || isDisabledDate(date, modifiers.disabled);

		props.onInspectionDateChangeHandler(selectedDay, isDisabled);
		if (isDisabled || !selectedDay) {
			props.setFormData('inspectionDate', null);
			return;
		}
		else {
			props.setFormData('inspectionDate', selectedDay);
		}
	}

	//#region style
	const modifiers={
		today: {},
		disabled:{
			before: new Date(moment(props.poCreationDate).subtract(90, 'days')),
			after: new Date(moment().subtract(0, 'days')),
		}
	}
	//#endregion

		const messages = defineMessages({
			crane_inspection__pass: {
				id: 'crane_inspection__pass',
				description: 'Inspection pass label',
				defaultMessage: 'PASS'
			},
			crane_inspection__fail: {
				id: 'crane_inspection__fail',
				description: 'Inspection fail label',
				defaultMessage: 'FAIL'
			},
			crane_inspection__has_inspection_passed_required: {
				id: 'crane_inspection__has_inspection_passed_required',
				description: 'Inspection pass/label label required',
				defaultMessage: 'Please select PASS or FAIL'
			},
			crane_inspection__has_inspection_passed_components_invalid: {
				id: 'crane_inspection__has_inspection_passed_components_invalid',
				description: 'Inspection passed but all items are not passed',
				defaultMessage: 'All vehicle components need to be marked as Sat. or N/A to pass inspection'
			},
			crane_inspection__has_inspection_passed_unsafe_conditions: {
				id: 'crane_inspection__has_inspection_passed_unsafe_conditions',
				description: 'Inspection passed but unsafe conditions was filled out',
				defaultMessage: 'The inspection cannot pass with unsafe conditions listed'
			},
			crane_inspection__has_inspection_passed_no_failure_specified: {
				id: 'crane_inspection__has_inspection_passed_no_failure_specified',
				description: 'Inspection failed but no item is marked as failed',
				defaultMessage: 'Please specify failed vehicle components and deficiencies'
			},
			crane_inspection__has_inspection_passed_no_failure_unsat_deficiencies: {
				id: 'crane_inspection__has_inspection_passed_no_failure_unsat_deficiencies',
				description: 'Inspection failed but no item is marked as failed',
				defaultMessage: 'Vehicle components marked as Sat. or N/A cannot have deficiencies listed'
			},
			crane_inspection__has_inspection_fail_deficiencies_without_unsat: {
				id: 'crane_inspection__has_inspection_fail_deficiencies_without_unsat',
				description: 'Inspection failed but no item is marked as unsat. for deficiencies',
				defaultMessage: 'Please specify the deficiencies for Unsat. vehicle components'
			},
			crane_inspection__has_inspection_failed_all_components_required: {
				id: 'crane_inspection__has_inspection_failed_all_components_required',
				description: 'Inspection failed but all components must specify Sat./Unsat./NA.',
				defaultMessage: 'All components must specify Sat./Unsat./NA.'
			},
			crane_inspection__inspection_date: {
				id: 'fra_inspection__inspection_date',
				description: 'Inspection date label',
				defaultMessage: 'Inspection date'
			},
			crane_inspection__inspection_date_invalid: {
				id: 'crane_inspection__inspection_date_invalid',
				description: 'Inspection date is invalid/incorrectly formatted',
				defaultMessage: 'Date invalid/incorrectly formatted, please enter a valid date'
			},
			crane_inspection__inspection_date_disabled: {
				id: 'crane_inspection__inspection_date_disabled',
				description: 'Inspection date disabled/out of range',
				defaultMessage: 'Please enter a valid date'
			},
			crane_inspection__inspector_attest: {
				id: 'crane_inspection__inspector_attest',
				description: 'Inspector attested label',
				defaultMessage: 'I attest that I am qualified to perform an ANSI and/or OSHA inspection and that this unit has passed all the ANSI and/or OSHA inspection requirements.'
			},
			crane_inspection__attest_required: {
				id: 'crane_inspection__attest_required',
				description: 'Validation: inspector did not attest',
				defaultMessage: 'Please attest to this statement'
			},
			crane_inspection__inspector_signature: {
				id: 'crane_inspection__inspector_signature',
				description: 'Inspector signature label',
				defaultMessage: "Inspector's signature"
			},
			crane_inspection__inspector_signature_note: {
				id: 'crane_inspection__inspector_signature_note',
				description: 'Inspector signature note',
				defaultMessage: "By typing your name, you agree this will serve as the electronic respresentation of your signature."
			},
			crane_inspection__signature_required: {
				id: 'crane_inspection__signature_required',
				description: 'Validation: inspector signature required',
				defaultMessage: 'Please enter your signature'
			},
		});

		return (
			<div>

				<div className="row no-gutters crane_result__result_code" >

					<div className="col-sm-2 no-gutters-col">
						<div className='crane_result__field_container' tabIndex='0'>
							<CheckCircle
								name='hasInspectionPassedTrue'
								checked={props.formData.get('hasInspectionPassed') === true}
								message={<FormattedMessage {...messages.crane_inspection__pass} />}
								onClick={handlePassCheckCircleClick} />
						</div>
					</div>

					<div className="col-sm-2 no-gutters-col">
						<div className='crane_result__field_container' tabIndex='0' ref={inspectionPassRef}>
							<CheckCircle
								name='hasInspectionPassedFalse'
								checked={props.formData.get('hasInspectionPassed') === false}
								onClick={handleFailCheckCircleClick}
								message={
									<FormattedMessage {...messages.crane_inspection__fail} />
								}
							/>

							{!!props.formValidationMessages.get('hasInspectionPassed') &&
								<Overlay
									show="true"
									target={inspectionPassRef.current}
									placement="right">
									<Popover
										id='hasInspectionPassedOverlay'
										className='crane_result__error_message'
									>
										<RawIntlProvider value={props.intl}>
											<FormattedMessage {...(messages[props.formValidationMessages.get('hasInspectionPassed')])} />
										</RawIntlProvider>
									</Popover>
								</Overlay>
							}
						</div>
					</div>

				</div>

				<div className='crane_result__inspection_date'>
					<div className='crane_result__field_container' tabIndex='0'>
						<div>
							<FormattedMessage {...messages.crane_inspection__inspection_date} />
						</div>
						<div className='crane_result__date_input' ref={inspectionDateRef}>
							<InputCalendar
								modifiers={modifiers}
								initialValue={props.formData.get('inspectionDate')}
								onDateChange={onDateChangeHandler}
								locale={props.userLocale}
							/>
							{!!props.formValidationMessages.get('inspectionDate') &&
								<Overlay
									show="true"
									target={inspectionDateRef.current}
									placement="auto">
									<Popover
										id='inspectionDateOverlay'
										className='crane_result__error_message'

									>
										<RawIntlProvider value={props.intl}>
											<FormattedMessage {...(messages[props.formValidationMessages.get('inspectionDate')])} />
										</RawIntlProvider>
									</Popover>
								</Overlay>
							}
						</div>
					</div>
				</div>

				{props.formData.get('hasInspectionPassed') !== false &&
					<div className='crane_result__attest'>
						<div className='crane_result__field_container' tabIndex='0' ref={attestRef}>
							<CheckCircle checked={props.formData.get('inspectorAttested')}
								name='inspectorAttested'
								message={<FormattedMessage {...messages.crane_inspection__inspector_attest} />}
								disabled={props.formData.get('hasInspectionPassed') === false}
								onClick={onClickAttest}
							/>
							{!!props.formValidationMessages.get('inspectorAttested') &&
								<Overlay
									show="true"
									target={attestRef.current}
									placement="bottom"
								>
									<Popover
										id='inspectorAttestedOverlay'
										className='crane_result__error_message'
									>
										<RawIntlProvider value={props.intl}>
											<FormattedMessage {...(messages[props.formValidationMessages.get('inspectorAttested')])} />
										</RawIntlProvider>
									</Popover>
								</Overlay>
							}
						</div>
					</div>
				}

				<div className='crane_result__inspector_signature' ref={signatureRef}>
					<div className='crane_result__field_container'>
						<FormattedMessage {...messages.crane_inspection__inspector_signature} />
						{' '}
						<input
							id='inspectorSignature'
							maxLength='50'
							value={props.formData.get('inspectorSignature')}
							onChange={handleChange}
							ref={signatureOverlayRef}
						/>
						<div className='crane_result__inspector_signature_note'>
							<FormattedMessage {...messages.crane_inspection__inspector_signature_note} />
						</div>
						{!!props.formValidationMessages.get('inspectorSignature') &&
							<Overlay
								show="true"
								target={signatureOverlayRef.current}
								placement="bottom">
								<Popover
									id='inspectorSignatureOverlay'
									className='crane_result__error_message'
								>
									<RawIntlProvider value={props.intl}>
										<FormattedMessage {...(messages[props.formValidationMessages.get('inspectorSignature')])} />
									</RawIntlProvider>
								</Popover>
							</Overlay>
						}
					</div>
				</div>

			</div>
		)
};

export default injectIntl(CraneResult);
